import { useMemo } from "react";
import Select, { components } from "react-select";

const CustomValueContainer = ({ children, ...props }) => {
  const selected = props.getValue();
  const isEmpty = selected.length === 0;
  const displayText = isEmpty
    ? props.selectProps.placeholder
    : selected.map((s) => s.label).join(", ");

  const isTyping = props.selectProps.inputValue.length > 0;

  return (
    <components.ValueContainer {...props}>
      {!isTyping && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            paddingLeft: "12px",
            paddingRight: "40px",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            pointerEvents: "none",
            color: isEmpty
              ? props.selectProps.placeholderColor ||
                props.theme.colors.neutral50 // default react-select placeholder color
              : props.theme.colors.neutral80, // normal text color
          }}
        >
          {displayText}
        </div>
      )}
      {children}
    </components.ValueContainer>
  );
};

const DropdownSet = ({
  value,
  options,
  label,
  name,
  disabled,
  required,
  onChange,
  isMulti,
  hiddenLabel = false,
  showMultiAsString = false,
  compact = false,
  variant = "default",
  placeholder,
  className,
}) => {
  const menuPortalTarget =
    typeof document !== "undefined" ? document.body : null;
  const transformedOptions = useMemo(
    () =>
      options.map((option) => ({
        label: option.name,
        value: option.id,
      })),
    [options]
  );

  const selectedValue = useMemo(() => {
    if (isMulti) {
      return transformedOptions.filter((option) =>
        value?.includes(option.value)
      );
    }
    return transformedOptions.find((option) => option.value === value) ?? null;
  }, [isMulti, transformedOptions, value]);

  const label_variants = {
    default: "focused:text-[#365BC9]",
  };

  let label_classes = `flex ${label_variants[variant]}`;

  const selectStyles = useMemo(() => {
    const controlMinHeight = compact ? 28 : 38;
    const valueContainerPaddingY = compact ? 0 : 2;
    const valueContainerPaddingX = compact ? 6 : 8;
    const indicatorPadding = compact ? 2 : 8;

    return {
      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
      menu: (base) => ({ ...base, zIndex: 9999 }),
      control: (base) => ({
        ...base,
        minHeight: controlMinHeight,
        height: "auto",
      }),
      valueContainer: (base) => ({
        ...base,
        position: showMultiAsString ? "relative" : base.position,
        paddingTop: valueContainerPaddingY,
        paddingBottom: valueContainerPaddingY,
        paddingLeft: valueContainerPaddingX,
        paddingRight: valueContainerPaddingX,
      }),
      indicatorsContainer: (base) => ({
        ...base,
        height: controlMinHeight,
      }),
      clearIndicator: (base) => ({ ...base, padding: indicatorPadding }),
      dropdownIndicator: (base) => ({ ...base, padding: indicatorPadding }),
      multiValue: (base) => (compact ? { ...base, margin: 2 } : base),
      multiValueLabel: (base) => (compact ? { ...base, padding: 2 } : base),
      multiValueRemove: (base) => (compact ? { ...base, padding: 2 } : base),
      input: (base) => (compact ? { ...base, margin: 0, padding: 0 } : base),
    };
  }, [compact, showMultiAsString]);

  return (
    <>
      <div
        className={`flex flex-col w-full gap-[6px] ${className || ""}`}
      >
        {!hiddenLabel && (
          <h2
            className={`${label_classes} ${disabled ? "text-[#D0D3D8]" : ""}`}
          >
            {label} {required && <span className="text-red-500">*</span>}
          </h2>
        )}
        <Select
          className="react-select"
          classNamePrefix="react-select"
          options={transformedOptions}
          isDisabled={disabled}
          name={name}
          value={selectedValue}
          isMulti={isMulti}
          isSearchable
          hideSelectedOptions={false}
          closeMenuOnSelect={!isMulti}
          menuPortalTarget={menuPortalTarget}
          menuPosition="fixed"
          menuShouldScrollIntoView={false}
          styles={selectStyles}
          controlShouldRenderValue={!showMultiAsString}
          {...(showMultiAsString
            ? {
                components: {
                  MultiValue: () => null,
                  Placeholder: () => null,
                  ValueContainer: CustomValueContainer,
                },
              }
            : {})}
          onChange={(e, action) => {
            if (isMulti) {
              onChange(
                name,
                e ? e.map((item) => item.value) : []
              );
            } else {
              if (action.action === "select-option" && selectedValue && e.value === selectedValue.value) {
                onChange(name, null);
              } else {
                e !== null ? onChange(name, e.value) : onChange(name, null);
              }
            }
          }}
          placeholder={placeholder || `Select ${label}`}
          isClearable
        />
      </div>
    </>
  );
};

export default DropdownSet;
